import type { MultiPayload } from "@defuse-protocol/contract-types"
import type { TokenInfo } from "../../../types/base"
import { assert } from "../../../utils/assert"
import {
  addAmounts,
  computeTotalBalanceDifferentDecimals,
  negateTokenValue,
  subtractAmounts,
} from "../../../utils/tokenUtils"
import type { TradeBreakdown } from "../types/sharedTypes"
import { type TradeTerms, deriveTradeTerms } from "./deriveTradeTerms"

export function computeTradeBreakdown(params: {
  multiPayload: MultiPayload
  tokenIn: TokenInfo
  tokenOut: TokenInfo
  protocolFee: number
}): TradeBreakdown {
  const tradeTerms = deriveTradeTerms(
    params.multiPayload,
    params.protocolFee
  ).unwrap()

  return computeTradeBreakdownFromTradeTerms({
    tradeTerms,
    tokenIn: params.tokenIn,
    tokenOut: params.tokenOut,
  })
}

export function computeTradeBreakdownFromTradeTerms(params: {
  tradeTerms: TradeTerms
  tokenIn: TokenInfo
  tokenOut: TokenInfo
}): TradeBreakdown {
  const { tradeTerms } = params

  let makerSends = computeTotalBalanceDifferentDecimals(
    params.tokenIn,
    tradeTerms.makerTokenDiff,
    { strict: false }
  )
  const makerReceives = computeTotalBalanceDifferentDecimals(
    params.tokenOut,
    tradeTerms.makerTokenDiff,
    { strict: false }
  )
  assert(makerSends != null && makerReceives != null)
  makerSends = negateTokenValue(makerSends)

  const makerPaysAppFee = computeTotalBalanceDifferentDecimals(
    params.tokenIn,
    tradeTerms.makerAppFee,
    {
      strict: false,
    }
  ) ?? {
    amount: 0n,
    decimals: makerSends.decimals,
  }

  const takerReceives = computeTotalBalanceDifferentDecimals(
    params.tokenIn, // tokenOut is tokenIn in the context of the taker
    tradeTerms.takerTokenDiff,
    { strict: false }
  )
  let takerSends = computeTotalBalanceDifferentDecimals(
    params.tokenOut, // tokenIn is tokenOut in the context of the taker
    tradeTerms.takerTokenDiff,
    { strict: false }
  )
  assert(takerSends != null && takerReceives != null)
  takerSends = negateTokenValue(takerSends)

  return {
    makerGrossSends: addAmounts(makerSends, makerPaysAppFee),
    makerSends,
    makerReceives,
    makerPaysAppFee,
    makerPaysFee: subtractAmounts(makerSends, takerReceives),
    takerSends,
    takerReceives,
    takerPaysFee: subtractAmounts(takerSends, makerReceives),
  }
}
